import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import api from '../utils/api';
import { useGoogleLogin } from '@react-oauth/google';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faBolt, faHistory, faCrosshairs, faVault, faCrown } from '@fortawesome/free-solid-svg-icons';
import { FaGoogle } from 'react-icons/fa';
import { formatGoogleAuthError, googleOriginHint } from '../utils/googleAuthErrors';
import { trackMetaCompleteRegistration } from '../utils/analytics';
import '../StylesComponents/signin-page.css';
import '../StylesComponents/signup-tier.css';

function SignUpPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('free');
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      console.log('📡 GOOGLE_RESPONSE_RECEIVED');
      try {
        const res = await api.post('/api/auth/google', { 
          token: tokenResponse.access_token 
        });
        const data = res.data;
        console.log('✅ BACKEND_AUTH_SUCCESS:', data.user.email);
        localStorage.setItem('token', data.accessToken);
        localStorage.setItem('user', JSON.stringify(data.user));
        if (data.isNewUser) trackMetaCompleteRegistration();
        if (onLogin) onLogin(data.user);
        navigate('/');
      } catch (err) {
        console.error('❌ GOOGLE_LOGIN_BACKEND_ERROR:', err.response?.data || err.message);
        alert('Google authentication failed. Please try again.');
      }
    },
    onError: (error) => {
      console.error('❌ GOOGLE_LOGIN_LIBRARY_ERROR:', error);
      alert(`Google sign-in failed: ${formatGoogleAuthError(error)}\n\n${googleOriginHint()}`);
    }
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const plan = params.get('plan');
    if (plan === 'pro') {
      // Pro is disabled for now, default to free
      setSelectedPlan('free');
    }
  }, [location]);

  const selectPlan = (plan) => {
    if (plan === 'pro') return; // Prevent selecting pro
    setSelectedPlan(plan);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    setIsProcessing(true);
    try {
      // Create Account
      const response = await api.post('/api/auth/signup', { 
        email, 
        username, 
        password, 
        accountType: 'customer' 
      });

      if (response.status === 201) {
        trackMetaCompleteRegistration();
        alert('Account created successfully. Please sign in.');
        navigate('/signin');
      }
    } catch (error) {
      console.error('Error during sign up:', error);
      const data = error.response?.data;
      alert(data?.error || 'Sign Up failed. Please check your inputs.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="signin-page-wrapper">
      <div className="signin-container glass-card animate-slide-up" style={{ maxWidth: '800px' }}>
        <div className="signin-status-tag">
          <span className="pulse-dot"></span>
          NEW ACCOUNT REGISTRATION
        </div>
        <h1 className="signin-heading">CHOOSE YOUR ACCESS</h1>

        <div className="plan-selection-container">
          <div 
            className={`plan-card ${selectedPlan === 'free' ? 'active' : ''}`}
            onClick={() => selectPlan('free')}
          >
            <div className="plan-header">
              <h3 className="plan-name">STOCKROOM_FREE</h3>
              <div className="plan-price" style={{ fontWeight: '900', letterSpacing: '2px' }}>FREE</div>
            </div>
            <ul className="plan-features">
              <li><FontAwesomeIcon icon={faCheck} /> ACCESS_AUSTRALIAN_MARKET: Real-time deals from thousands of premium products.</li>
              <li><FontAwesomeIcon icon={faCheck} /> CUSTOM_WATCHLIST: Monitor and track price changes on your favorite items.</li>
              <li><FontAwesomeIcon icon={faCheck} /> BASIC_DROP_SIGNALS: Standard notifications for new market markdowns.</li>
              <li><FontAwesomeIcon icon={faCheck} /> CURATED_RETAILERS: Browse verified deals from over 25+ top brands.</li>
            </ul>
          </div>

          <div 
            className={`plan-card plan-card-pro disabled ${selectedPlan === 'pro' ? 'active' : ''}`}
            onClick={() => selectPlan('pro')}
          >
            <div className="plan-header">
              <h3 className="plan-name">STOCKROOM_PRO</h3>
              <div className="plan-price">$4.99 / Month</div>
            </div>
            <ul className="plan-features">
              <li><FontAwesomeIcon icon={faBolt} /> THE_SIGNAL: Gain an elite 3-hour head start on every new drop.</li>
              <li><FontAwesomeIcon icon={faHistory} /> MARKET_HISTORY: Access technical price charts to verify record-low value.</li>
              <li><FontAwesomeIcon icon={faCrosshairs} /> AUTO_SNIPE_BOT: Lock custom parameters to automate your deal acquisition.</li>
              <li><FontAwesomeIcon icon={faVault} /> THE_VAULT: Access secret, stackable discount codes for maximum reduction.</li>
            </ul>
            <div className="plan-footer-pro" style={{ marginTop: '20px', borderTop: '1px solid #222', paddingTop: '15px' }}>
              <div className="learn-more-pro disabled-link">
                LEARN_MORE_ABOUT_PRO <FontAwesomeIcon icon={faCrown} style={{ marginLeft: '5px', fontSize: '10px' }} />
              </div>
            </div>
          </div>
        </div>

        <form className="signin-form" onSubmit={handleSubmit} style={{ borderTop: '1px solid #111', paddingTop: '40px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div className="input-group">
              <label htmlFor="email">EMAIL ADDRESS</label>
              <input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ENTER EMAIL" required />
            </div>
            <div className="input-group">
              <label htmlFor="username">USERNAME</label>
              <input type="text" id="username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="ENTER USERNAME" required />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '10px' }}>
            <div className="input-group">
              <label htmlFor="password">PASSWORD</label>
              <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="ENTER PASSWORD" required />
            </div>
            <div className="input-group">
              <label htmlFor="confirmPassword">CONFIRM PASSWORD</label>
              <input type="password" id="confirmPassword" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="CONFIRM PASSWORD" required />
            </div>
          </div>

          <button type="submit" className="brutalist-btn brutalist-btn-primary signin-button" style={{ marginTop: '30px' }} disabled={isProcessing}>
            {isProcessing ? 'PROCESSING_SECURE_SIGNAL...' : 'CREATE FREE ACCOUNT'}
          </button>
        </form>

        <div className="auth-links">
          <div className="signup-link">
            <p>ALREADY HAVE AN ACCOUNT? <a href="/signin">SIGN IN HERE</a></p>
          </div>

          <div className="social-auth-separator" style={{ margin: '20px 0' }}>
            <span className="separator-line"></span>
            <span className="separator-text">OR_CONNECT_VIA_SOCIAL</span>
            <span className="separator-line"></span>
          </div>

          <div className="social-auth-grid" style={{ gridTemplateColumns: '1fr' }}>
            <button className="social-auth-btn google" type="button" onClick={() => googleLogin()}>
              <FaGoogle /> SIGN_UP_WITH_GOOGLE
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignUpPage;
